import { FormActionFrameworkStep, registerStep } from '../step';
import { resolveTemplate } from '../template';
export class SetVariableStep extends FormActionFrameworkStep {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'setVariable'
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                variableName: '',
                value: '',
                valueType: 'string',
            }
        });
    }
    async execute(context) {
        const nameRaw = (typeof this.config.variableName === 'string' && this.config.variableName.trim()) ||
            '';
        const name = String(resolveTemplate(nameRaw, context) ?? '').trim();
        if (!name) {
            context.log('[setVariable] missing variableName — skipped');
            return context;
        }
        const rawValue = this.config.value ?? '';
        const resolved = resolveTemplate(rawValue, context);
        const valueType = this.config.valueType || 'string';
        const coerced = coerceValue(resolved, valueType, context);
        context.variables.set(name, coerced);
        context.log(`[setVariable] ${name} = ${summarize(coerced)} (${valueType})`);
        return context;
    }
    showStepForm(container, _context, onUpdated) {
        const cfg = this.config;
        const valueType = cfg.valueType || 'string';
        container.innerHTML = `
      <div class="mb-3">
        <label class="form-label">Step name</label>
        <input type="text" class="form-control" data-faf="name" value="${escapeAttr(this.name)}" />
      </div>
      <div class="faf-id-row mb-3">
        <div class="faf-id-field faf-id-field--name">
          <label class="form-label">Variable name</label>
          <input type="text" class="form-control" data-faf="variableName" value="${escapeAttr(cfg.variableName)}" placeholder="variable_name" />
        </div>
        <div class="faf-id-field faf-id-field--type">
          <label class="form-label">Value type</label>
          <select class="form-control" data-faf="valueType">
            <option value="string"${valueType === 'string' ? ' selected' : ''}>string</option>
            <option value="number"${valueType === 'number' ? ' selected' : ''}>number</option>
            <option value="object"${valueType === 'object' ? ' selected' : ''}>object</option>
          </select>
        </div>
      </div>
      <div class="mb-3">
        <label class="form-label">Value</label>
        <textarea class="form-control" data-faf="value" rows="3" placeholder='literal, {{expression}}, or {"a":1}'>${escapeText(String(cfg.value ?? ''))}</textarea>
        <div class="text-muted small">Supports {{ }} expressions against context variables.</div>
      </div>
    `;
        const sync = () => {
            this.name = inputValue(container, 'name');
            this.config.variableName = inputValue(container, 'variableName');
            this.config.valueType = (selectValue(container, 'valueType') ||
                'string');
            this.config.value = textareaValue(container, 'value');
            onUpdated?.();
        };
        container.querySelectorAll('input, textarea, select').forEach((el) => {
            el.addEventListener('change', sync);
            el.addEventListener('blur', sync);
        });
    }
}
function coerceValue(resolved, valueType, context) {
    if (valueType === 'string') {
        if (resolved == null)
            return '';
        if (typeof resolved === 'object')
            return JSON.stringify(resolved);
        return String(resolved);
    }
    if (valueType === 'number') {
        if (typeof resolved === 'number')
            return resolved;
        const n = Number(resolved);
        if (!Number.isFinite(n)) {
            context.log(`[setVariable] could not coerce to number: ${String(resolved)}`);
            return null;
        }
        return n;
    }
    // object
    if (resolved != null && typeof resolved === 'object')
        return resolved;
    if (typeof resolved === 'string') {
        const trimmed = resolved.trim();
        if (!trimmed)
            return null;
        try {
            return JSON.parse(trimmed);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            context.log(`[setVariable] object JSON parse failed: ${message}`);
            throw new Error(`setVariable object value is not valid JSON: ${message}`);
        }
    }
    return resolved;
}
function summarize(value) {
    if (value == null)
        return String(value);
    if (typeof value === 'object') {
        try {
            const json = JSON.stringify(value);
            return json.length > 120 ? `${json.slice(0, 117)}...` : json;
        }
        catch {
            return '[object]';
        }
    }
    return String(value);
}
function inputValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value?.trim() ?? '';
}
function selectValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value ?? '';
}
function textareaValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value ?? '';
}
function escapeAttr(value) {
    if (value == null)
        return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
}
function escapeText(value) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;');
}
registerStep('setVariable', SetVariableStep);
